import { translateText } from '@/i18n'
import {
  clearanceSale,
  getBbAssetBySymbol,
  getBbProfitChart,
  getCoinBalance,
  getDealOrderDetails,
  getDealOrderList,
  getFuturesXDetail,
  getFuturesXOrder,
  getFuturesXPosition,
  getFuturesXProfitChart,
  getFuturesProfitChart,
  getFuturesSpotAIPnl,
  getGridStrategy,
  getIndex,
  getMartinDealOrderDetails,
  getMartinStrategy,
  getNewStrategyDetail,
  getNewStrategyOrder,
  getNewStrategyPosition,
  getNewStrategyProfit,
  getNoDealSellOrderList,
  getOrderListByStateAndSymbol,
  getStrategyCircleList,
  getSupremeHedge,
  getSupremeHedgeOrder,
  getSupremeHedgeSpace,
  getSymbolInfoByCoins,
  getTickerPrice,
  martinStrategyShortcut,
  oneClickReplenishment,
  orderListHedging,
  positionTermHedgin,
  profitTermHedging,
  stopReplenishment
} from '@/server/robot'
import {
  robotProductType,
  robotTypeValue
} from '@/api/robot-actions'
import { loadOrbitStrategyDetail } from '@/api/orbit-strategy'

function finiteNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function firstValue(source, keys, fallback = '') {
  for (const key of keys) {
    const value = source && source[key]
    if (value !== '' && value !== null && value !== undefined) return value
  }
  return fallback
}

function strategyRaw(robot) {
  return robot && robot.raw && typeof robot.raw === 'object' ? robot.raw : {}
}

function strategySid(robot) {
  const raw = strategyRaw(robot)
  return firstValue(raw, ['sid', 'strategy_uid', 'uid', 'id'], robot && robot.id)
}

function strategyUid(robot) {
  const raw = strategyRaw(robot)
  return firstValue(raw, ['strategy_uid', 'uid', 'sid', 'id'], robot && robot.id)
}

function strategyCoin(robot) {
  const raw = strategyRaw(robot)
  return firstValue(raw, ['coin_two', 'base_coin', 'coin', 'symbol'])
}

function strategyVersion(robot) {
  const raw = strategyRaw(robot)
  return firstValue(raw, ['old_new', 'version', 'version_type', 'versionType'])
}

function selectExchange(robot) {
  const robotType = robotTypeValue(robot)
  getIndex(robotType === 0 ? 1 : 2)
  return robotType
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  const keys = [
    'list',
    'records',
    'rows',
    'content',
    'positionList',
    'orderList',
    'data'
  ]
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key]
  }
  if (
    payload.symbol ||
    payload.instrument_id ||
    payload.instId ||
    payload.strategy_uid
  ) {
    return [payload]
  }
  return []
}

function extractTotal(payload, fallback = 0) {
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') return fallback
  const value = firstValue(
    payload,
    ['totalRow', 'total', 'totalElements', 'count', 'totalCount'],
    fallback
  )
  return finiteNumber(value) === null ? fallback : Number(value)
}

function normalizeProfitPoints(payload) {
  if (payload && !Array.isArray(payload) && typeof payload === 'object') {
    const dates = firstValue(payload, ['dates', 'dateList', 'timeList', 'xAxis'], [])
    const values = firstValue(payload, ['profits', 'profitList', 'valueList', 'yAxis'], [])
    if (Array.isArray(dates) && Array.isArray(values)) {
      return dates.reduce((rows, date, index) => {
        const value = finiteNumber(values[index])
        if (value !== null) rows.push({ date: String(date), value })
        return rows
      }, [])
    }
  }
  return extractList(payload).reduce((rows, item, index) => {
    const value = finiteNumber(firstValue(
      item,
      ['profit', 'profit_total', 'value', 'income', 'earn']
    ))
    if (value !== null) {
      rows.push({
        date: String(firstValue(
          item,
          ['profit_day', 'date', 'day', 'time', 'create_time', 'add_time'],
          index + 1
        )),
        value,
        yield: finiteNumber(firstValue(
          item,
          ['profit_yield', 'profit_rate', 'yield_rate']
        ))
      })
    }
    return rows
  }, [])
}

async function call(label, promise) {
  try {
    const response = await promise
    if (!response) {
      return { label, data: null, error: translateText(`${label}接口未返回数据`) }
    }
    if (response.success === false) {
      return {
        label,
        data: null,
        error: response.message || translateText(`${label}读取失败`)
      }
    }
    return { label, data: response.data, error: '' }
  } catch (error) {
    return {
      label,
      data: null,
      error: (error && error.message) || translateText(`${label}读取失败`)
    }
  }
}

function collectErrors(results) {
  return results.map(result => result.error).filter(Boolean)
}

function commonResult(robot) {
  return {
    detail: { ...strategyRaw(robot) },
    positions: [],
    positionTotal: 0,
    pendingOrders: [],
    pendingOrderTotal: 0,
    orders: [],
    orderTotal: 0,
    chart: [],
    marketPrice: null,
    errors: [],
    detailKind: robotProductType(robot)
  }
}

function autoProductNumber(type) {
  if (type === 'sj') return 3
  if (type === 'bb') return 2
  return 1
}

function autoSymbol(robot, type) {
  const raw = strategyRaw(robot)
  if (type === 'sj') return firstValue(raw, ['belong_to', 'coin', 'symbol'])
  return firstValue(raw, ['coin', 'symbol'])
}

export async function loadStrategyDetail(robot, options = {}) {
  const result = commonResult(robot)
  const isHistory = Boolean(strategyRaw(robot).__historyStrategy)
  const type = robotProductType(robot)
  if (type === 'orbit') return { ...result, ...await loadOrbitStrategyDetail(robot, options) }
  const robotType = selectExchange(robot)
  const sid = strategySid(robot)
  const uid = strategyUid(robot)
  const page = Number(options.page) || 1
  const pageSize = Number(options.pageSize) || 10
  const positionPage = Number(options.positionPage) || 1
  const positionPageSize = Number(options.positionPageSize) || pageSize
  const requestTimes = Number(options.requestTimes) || 0
  const only = typeof options.only === 'string' ? options.only : 'all'
  const wants = section => only === 'all' || only === section
  const run = (section, label, request) => wants(section)
    ? call(label, request())
    : Promise.resolve({ label, data: null, error: '', skipped: true })

  if (type === 'astra') {
    const month = options.month || new Date().toISOString().slice(0, 7)
    const responses = await Promise.all([
      run('detail', '策略详情', () => getNewStrategyDetail(robotType, sid)),
      run('positions', '持仓明细', () => getNewStrategyPosition(robotType, sid, options.positionState || 0)),
      run('orders', '成交记录', () => getNewStrategyOrder(
        robotType,
        sid,
        options.orderState || 0,
        page,
        pageSize
      )),
      run('chart', '收益曲线', () => getNewStrategyProfit(robotType, month, sid))
    ])
    const [detail, positions, orders, chart] = responses
    result.detail = { ...result.detail, ...(detail.data || {}) }
    result.positions = extractList(positions.data)
    result.positionTotal = extractTotal(positions.data, result.positions.length)
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.chart = normalizeProfitPoints(chart.data)
    result.errors = collectErrors(responses)
    return result
  }

  if (type === 'hedge') {
    const responses = await Promise.all([
      run('detail', '策略详情', () => getSupremeHedge(robotType, sid)),
      run('positions', '持仓明细', () => getSupremeHedgeSpace(
        options.positionState || '0',
        options.positionStartDate || '',
        options.positionEndDate || '',
        positionPage,
        positionPageSize,
        robotType,
        sid
      )),
      run('orders', '委托记录', () => getSupremeHedgeOrder(
        options.orderState || 0,
        options.orderStartDate || '',
        options.orderEndDate || '',
        page,
        pageSize,
        robotType,
        options.orderType || 2,
        sid
      )),
      run('chart', '收益曲线', () => getFuturesProfitChart(
        robotType,
        sid,
        options.month || new Date().toISOString().slice(0, 7)
      ))
    ])
    const [detail, positions, orders, chart] = responses
    result.detail = { ...result.detail, ...(detail.data || {}) }
    result.positions = extractList(positions.data)
    result.positionTotal = extractTotal(positions.data, result.positions.length)
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.chart = normalizeProfitPoints(chart.data)
    result.errors = collectErrors(responses)
    return result
  }

  if (type === 'futures_x') {
    const responses = await Promise.all([
      run('detail', '策略详情', () => getFuturesXDetail(robotType, sid)),
      run('positions', '持仓明细', () => getFuturesXPosition(robotType, sid)),
      run('orders', '委托记录', () => getFuturesXOrder(
        page,
        pageSize,
        robotType,
        sid,
        options.orderState || 0
      )),
      run('chart', '收益曲线', () => getFuturesXProfitChart(
        robotType,
        sid,
        options.month || new Date().toISOString().slice(0, 7)
      ))
    ])
    const [detail, positions, orders, chart] = responses
    result.detail = { ...result.detail, ...(detail.data || {}) }
    result.positions = extractList(positions.data)
    result.positionTotal = extractTotal(positions.data, result.positions.length)
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.chart = normalizeProfitPoints(chart.data)
    result.errors = collectErrors(responses)
    return result
  }

  if (type === 'term') {
    const coin = strategyCoin(robot)
    const version = strategyVersion(robot)
    const termUid = firstValue(strategyRaw(robot), ['uid'], uid)
    const termSid = isHistory ? firstValue(strategyRaw(robot), ['sid'], sid) : undefined
    const responses = await Promise.all([
      run('detail', '策略收益', () => profitTermHedging({
        robot_type: robotType,
        coin,
        version,
        ...(termSid !== undefined && termSid !== '' ? { sid: termSid } : {})
      })),
      run('positions', '持仓明细', () => positionTermHedgin(coin, robotType, version, termSid)),
      run('orders', '委托记录', () => orderListHedging(robotType, coin, version, page, pageSize, termSid)),
      isHistory
        ? Promise.resolve({ label: '浮动盈亏', data: null, error: '', skipped: true })
        : run('detail', '浮动盈亏', () => getFuturesSpotAIPnl(robotType, termUid))
    ])
    const [detail, positions, orders, pnl] = responses
    result.detail = {
      ...result.detail,
      ...(detail.data || {}),
      ...(pnl.data || {}),
      floating_pnl: firstValue(
        pnl.data || {},
        ['pnl'],
        firstValue(result.detail, ['floating_pnl', 'profit_lose'], null)
      )
    }
    result.positions = extractList(positions.data)
    result.positionTotal = extractTotal(positions.data, result.positions.length)
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.errors = collectErrors(responses)
    return result
  }

  if (type === 'grid') {
    const raw = strategyRaw(robot)
    const responses = await Promise.all([
      run('detail', '策略详情', () => getGridStrategy(uid, requestTimes)),
      run('pending', '买入委托', () => getNoDealSellOrderList(uid, 'buy', requestTimes)),
      run('pending', '卖出委托', () => getNoDealSellOrderList(uid, 'sell', requestTimes)),
      run('orders', '成交记录', () => getDealOrderList(uid, page, pageSize, requestTimes)),
      run('detail', '最新行情', () => getTickerPrice(
        firstValue(raw, ['coin', 'symbol']),
        robotType,
        requestTimes
      ))
    ])
    const [detail, buyOrders, sellOrders, orders, market] = responses
    result.detail = { ...result.detail, ...(detail.data || {}) }
    result.pendingOrders = [
      ...extractList(buyOrders.data).map(item => ({ ...item, __pendingSide: 'buy' })),
      ...extractList(sellOrders.data).map(item => ({ ...item, __pendingSide: 'sell' }))
    ]
    result.pendingOrderTotal = result.pendingOrders.length
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.marketPrice = finiteNumber(market.data)
    result.errors = collectErrors(responses)
    return result
  }

  if (type === 'martin') {
    const responses = await Promise.all([
      run('detail', '策略详情', () => getMartinStrategy(uid)),
      run('orders', '循环记录', () => getStrategyCircleList(uid, page, pageSize, requestTimes))
    ])
    const [detail, orders] = responses
    result.detail = { ...result.detail, ...(detail.data || {}) }
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.errors = collectErrors(responses)
    return result
  }

  if (['sj', 'bb', 'ub'].includes(type)) {
    const raw = strategyRaw(robot)
    const selectedOrderState = options.orderState === undefined ? 0 : options.orderState
    const selectedOrders = run('orders', '委托/成交记录', () => getOrderListByStateAndSymbol(
      selectedOrderState,
      selectedOrderState === 0 ? '' : options.orderStartDate || '',
      selectedOrderState === 0 ? '' : options.orderEndDate || '',
      autoProductNumber(type),
      autoSymbol(robot, type),
      page,
      pageSize,
      robotType,
      firstValue(raw, ['uid_one']),
      firstValue(raw, ['uid_two'])
    ))
    const responses = await Promise.all([
      selectedOrders,
      run('detail', '交易所资产', () => getBbAssetBySymbol(
        robotType,
        firstValue(raw, ['coin_one']),
        firstValue(raw, ['coin_two'])
      )),
      run('chart', '收益曲线', () => getBbProfitChart(
        firstValue(raw, ['uid_one']),
        firstValue(raw, ['uid_two']),
        robotType,
        options.month || new Date().toISOString().slice(0, 7)
      ))
    ])
    const [orders, asset, chart] = responses
    result.detail = { ...result.detail, ...(asset.data || {}) }
    result.orders = extractList(orders.data)
    result.orderTotal = extractTotal(orders.data, result.orders.length)
    result.chart = normalizeProfitPoints(chart.data)
    result.errors = collectErrors(responses)
    return result
  }

  return result
}

export async function loadStrategyOrderDetail(robot, row) {
  selectExchange(robot)
  const type = robotProductType(robot)
  const uid = strategyUid(robot)
  let response
  if (type === 'grid') {
    response = await getDealOrderDetails(
      uid,
      firstValue(row, ['buy_order_id', 'buyOrderId']),
      firstValue(row, ['sell_order_id', 'sellOrderId'])
    )
  } else if (type === 'martin') {
    response = await getMartinDealOrderDetails(
      uid,
      firstValue(row, ['multiplex']),
      firstValue(row, ['number_of_cycles', 'numberOfCycles'])
    )
  } else {
    return []
  }
  if (!response || response.success === false) {
    throw new Error((response && response.message) || translateText('成交明细读取失败'))
  }
  return extractList(response.data)
}

export async function loadMartinReplenishmentContext(robot, detail = {}) {
  const robotType = selectExchange(robot)
  const raw = { ...strategyRaw(robot), ...detail }
  const coinText = firstValue(raw, ['coin', 'symbol'])
  const parts = String(coinText || '').split(/[-/]/).filter(Boolean)
  const quoteCoin = firstValue(raw, ['coin_one', 'quote_coin'], parts[0])
  const baseCoin = firstValue(raw, ['coin_two', 'base_coin'], parts[1])
  const responses = await Promise.all([
    call('最新行情', getTickerPrice(coinText, robotType, 0)),
    call('可用余额', getCoinBalance(quoteCoin, robotType)),
    call('最小下单量', getSymbolInfoByCoins(quoteCoin, baseCoin))
  ])
  const [market, balance, symbolInfo] = responses
  const errors = collectErrors(responses)
  return {
    price: finiteNumber(market.data),
    balance: finiteNumber(balance.data),
    minQuantity: finiteNumber(firstValue(symbolInfo.data || {}, ['min_qty', 'minQty'])),
    quoteCoin,
    errors
  }
}

export async function executeStrategyDetailCommand({
  robot,
  command,
  action = 0,
  amount,
  price
}) {
  selectExchange(robot)
  if (robotProductType(robot) !== 'martin') {
    throw new Error(translateText('当前策略没有该专项操作'))
  }
  const uid = strategyUid(robot)
  let response
  if (command === 'shortcut') {
    response = await martinStrategyShortcut(uid, action)
  } else if (command === 'replenishment') {
    response = await stopReplenishment(uid, action)
  } else if (command === 'clearance') {
    response = await clearanceSale(uid, action)
  } else if (command === 'one-click') {
    response = await oneClickReplenishment(uid, action, amount, price)
  } else {
    throw new Error(translateText('未知的策略操作'))
  }
  if (!response || response.success === false) {
    throw new Error((response && response.message) || translateText('策略操作失败'))
  }
  return response
}
